import { useMemo, useState } from 'react';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import type { PaginationMeta } from '@/types/api';

export const ROWS_PER_PAGE = [10, 25, 50] as const;

/** One register's filters, as they sit in the address bar under its prefix. */
type Filters = {
  search: string;
  status: string;
  page: number;
  limit: number;
};

const DEFAULT_FILTERS: Filters = {
  search: '',
  status: '',
  page: 1,
  limit: ROWS_PER_PAGE[0],
};

export interface UseRegisterOptions<T> {
  /**
   * Namespaces this register's filters in the address — every register on the
   * page keeps its own, so one tab never reads another's search.
   */
  prefix: string;
  /**
   * Every status a row can hold — what a status in the address may be. Must be
   * a stable reference (module level), like any `useUrlFilters` option.
   */
  statuses?: readonly string[];
  /** Everything the tab was handed, before searching or filtering. */
  rows: T[];
  /** Which of a row's fields the search box looks at. Nulls are skipped. */
  fieldsOf: (row: T) => Array<string | null>;
  /** A row's status, for the filter. Omit on a register with no status. */
  statusOf?: (row: T) => string;
}

export interface Register<T> {
  query: string;
  setQuery: (value: string) => void;
  status: string;
  setStatus: (value: string) => void;
  isFilterOpen: boolean;
  toggleFilter: () => void;
  /** Everything matching the search and filter — what the cards read. */
  visible: T[];
  /** Just the current page of it — what the table reads. */
  page: T[];
  meta: PaginationMeta;
  goToPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

/**
 * The searching, filtering and paging every register in this section does.
 *
 * Written once because the Routes, Vehicles and Drivers tabs were each about to
 * hold the same six pieces of state and the same two `slice` calls, differing
 * only in which fields they search (CLAUDE.md §3). The columns are what makes a
 * register its own screen; this is not.
 *
 * **Paging is in the browser, deliberately — for now.** The whole section reads
 * a mock. When the endpoints land this hook is where server paging goes, and no
 * tab above it has to change.
 */
export function useRegister<T>({
  prefix,
  statuses,
  rows,
  fieldsOf,
  statusOf,
}: UseRegisterOptions<T>): Register<T> {
  // Kept in the address, so Back from a record returns to the same search,
  // status and page. Memoised on its inputs because the hook needs a stable
  // options object, and each caller hands in its own prefix and statuses.
  const filterOptions = useMemo(
    () => ({ prefix, allowed: { status: statuses, limit: ROWS_PER_PAGE } }),
    [prefix, statuses],
  );
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, filterOptions);
  const { search: query, status, page, limit } = filters;
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesQuery =
        !needle || fieldsOf(row).some((field) => field?.toLowerCase().includes(needle));
      return matchesQuery && (!status || statusOf?.(row) === status);
    });
    // `fieldsOf` and `statusOf` are declared inline by every caller, so a new
    // identity arrives on each render — depending on them would rebuild this
    // list every time. What the result actually depends on is below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, status]);

  const totalPages = Math.max(1, Math.ceil(visible.length / limit));
  // A search that narrows the list while the reader is on page 3 would
  // otherwise ask for a page that no longer exists, and show an empty table.
  const currentPage = Math.min(page, totalPages);

  return {
    query,
    setQuery: (value) => setFilters({ search: value, page: 1 }),
    status,
    setStatus: (value) => setFilters({ status: value, page: 1 }),
    isFilterOpen,
    toggleFilter: () => setIsFilterOpen((open) => !open),
    visible,
    page: visible.slice((currentPage - 1) * limit, currentPage * limit),
    meta: { page: currentPage, limit, total: visible.length, totalPages },
    goToPage: (next) => setFilters({ page: next }),
    setLimit: (next) => setFilters({ limit: next, page: 1 }),
  };
}
